import { useTexture } from '@react-three/drei';
import { GridObjectType, GridSpaceType } from 'data/types';
import { useMemo } from 'react';
import { MeshStandardMaterial, RepeatWrapping } from 'three';
import { getElectricalPanel3DPlacement } from 'utils/3d-equipment';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';
import electricalPanelSideTexture from './assets/electrical-panel-side.jpg';
import electricalPanelTopTexture from './assets/electrical-panel-top.jpg';

type ElectricalPanel3DProps = Readonly<{
  object: GridObjectType;
  spaces?: GridSpaceType[];
  yOffset?: number;
  opacity?: number;
}>;

export default function ElectricalPanel3D({
  object,
  spaces = [],
  yOffset = 0,
  opacity = 1
}: ElectricalPanel3DProps) {
  const [sideTexture, topTexture] = useTexture([
    electricalPanelSideTexture,
    electricalPanelTopTexture
  ]);

  const width = 0.5;
  const depth = 1;
  const height = 2;

  const {
    x,
    y: yPosition,
    z,
    rotation
  } = useMemo(
    () => getElectricalPanel3DPlacement(object, spaces, height),
    [object, spaces, height]
  );

  const materials = useMemo(() => {
    const createMaterial = (
      texture: typeof sideTexture,
      repeatX: number,
      repeatY: number,
      textureRotation = 0
    ) => {
      const texClone = texture.clone();
      texClone.wrapS = RepeatWrapping;
      texClone.wrapT = RepeatWrapping;
      texClone.repeat.set(repeatX, repeatY);
      texClone.rotation = textureRotation;
      texClone.center.set(0.5, 0.5);
      texClone.needsUpdate = true;
      return new MeshStandardMaterial({
        map: texClone,
        ...getOpacityMaterialProps(opacity)
      });
    };

    return [
      createMaterial(sideTexture, 1, 1),
      createMaterial(sideTexture, 1, 1),
      createMaterial(topTexture, 1, 1, Math.PI / 2),
      createMaterial(topTexture, 1, 1, Math.PI / 2),
      createMaterial(sideTexture, 1, 1),
      createMaterial(sideTexture, 1, 1)
    ];
  }, [sideTexture, topTexture, opacity]);

  return (
    <group position={[x, yPosition + yOffset, z]} rotation={[0, rotation, 0]}>
      <mesh material={materials} renderOrder={getOpacityRenderOrder(opacity)}>
        <boxGeometry args={[width, height, depth]} />
      </mesh>
    </group>
  );
}
